import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Drop, X, Check, Snowflake, Cube, GridFour, MagnifyingGlass, ArrowLeft, Trash
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TOWERS = 'ABCDEFGH'.split('');

// ==================== SLOT GRID (9x9) ====================
const SlotGrid = ({ slots, onSlotClick, selectedSlot, searchTerm }) => {
  const filtered = searchTerm
    ? slots.filter(s => s.sample_label?.toLowerCase().includes(searchTerm.toLowerCase()))
    : null;

  return (
    <div className="ln2-grid" data-testid="slot-grid">
      {/* Column headers */}
      <div className="ln2-grid-header">
        <div className="ln2-corner" />
        {[1,2,3,4,5,6,7,8,9].map(c => (
          <div key={c} className="ln2-col-label">{c}</div>
        ))}
      </div>
      {/* Rows */}
      {[1,2,3,4,5,6,7,8,9].map(row => (
        <div key={row} className="ln2-grid-row">
          <div className="ln2-row-label">{String.fromCharCode(64 + row)}</div>
          {[1,2,3,4,5,6,7,8,9].map(col => {
            const slot = slots.find(s => s.row === row && s.col === col);
            if (!slot) return <div key={col} className="ln2-slot ln2-slot--empty" />;
            const isSelected = selectedSlot === slot.slot;
            const isHighlighted = filtered && filtered.some(f => f.slot === slot.slot);
            const dimmed = filtered && !isHighlighted;
            return (
              <button
                key={col}
                onClick={() => onSlotClick(slot)}
                className={`ln2-slot ${
                  slot.occupied ? 'ln2-slot--occupied' : 'ln2-slot--free'
                } ${isSelected ? 'ln2-slot--selected' : ''} ${dimmed ? 'ln2-slot--dimmed' : ''} ${isHighlighted ? 'ln2-slot--highlighted' : ''}`}
                title={slot.occupied ? `${slot.sample_label || 'Sample'} (${slot.sample_type || ''})` : `Empty - ${String.fromCharCode(64 + row)}${col}`}
                data-testid={`slot-${slot.slot}`}
              >
                {slot.occupied && <div className="ln2-vial" />}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ==================== SLOT DETAIL PANEL ====================
const SlotPanel = ({ tower, boxNum, slot, onClose, onRefresh }) => {
  const [form, setForm] = useState({
    sample_label: slot.sample_label || '',
    sample_type: slot.sample_type || '',
    notes: slot.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const row = String.fromCharCode(64 + slot.row);

  const assign = async () => {
    if (!form.sample_label.trim()) { toast.error('Sample label is required'); return; }
    setSaving(true);
    try {
      await api.updateLN2Slot(tower, boxNum, slot.slot, {
        action: 'assign',
        sample_label: form.sample_label.trim(),
        sample_type: form.sample_type.trim(),
        notes: form.notes.trim(),
      });
      toast.success(`Vial stored at ${tower}${boxNum}-${row}${slot.col}`);
      onRefresh();
      onClose();
    } catch { toast.error('Failed to assign slot'); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Remove vial from ${tower}${boxNum}-${row}${slot.col}?`)) return;
    setClearing(true);
    try {
      await api.updateLN2Slot(tower, boxNum, slot.slot, { action: 'clear' });
      toast.success('Slot cleared');
      onRefresh();
      onClose();
    } catch { toast.error('Failed to clear slot'); }
    finally { setClearing(false); }
  };

  return (
    <div className="ln2-panel" data-testid="slot-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-100">
          Slot {tower}{boxNum} — {row}{slot.col}
        </h3>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X size={18} /></button>
      </div>

      {slot.occupied && (
        <div className="p-3 rounded-lg bg-cyan-500/8 border border-cyan-500/20 mb-4">
          <p className="text-sm font-medium text-cyan-300">{slot.sample_label}</p>
          {slot.sample_type && <p className="text-xs text-slate-400 mt-0.5">{slot.sample_type}</p>}
          {slot.stored_by && (
            <p className="text-xs text-slate-500 mt-1">Stored by {slot.stored_by} on {new Date(slot.stored_at).toLocaleDateString()}</p>
          )}
          {slot.notes && <p className="text-xs text-slate-500 mt-1">{slot.notes}</p>}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400">Sample Label *</Label>
          <Input value={form.sample_label} onChange={e => setForm({...form, sample_label: e.target.value})}
            placeholder="e.g. HEK293-GFP-C1" className="input-field w-full mt-1" data-testid="slot-sample-label" />
        </div>
        <div>
          <Label className="text-xs text-slate-400">Sample Type</Label>
          <select value={form.sample_type} onChange={e => setForm({...form, sample_type: e.target.value})}
            className="input-field w-full mt-1" data-testid="slot-sample-type">
            <option value="">Select type...</option>
            <option value="cell_line">Cell Line</option>
            <option value="plasmid">Plasmid Stock</option>
            <option value="strain">Bacterial Strain</option>
            <option value="virus">Virus Stock</option>
            <option value="protein">Protein</option>
            <option value="tissue">Tissue</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-400">Notes</Label>
          <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
            placeholder="Passage #, concentration, etc." className="input-field w-full mt-1" data-testid="slot-notes" />
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button onClick={assign} className="btn-primary flex-1" disabled={saving} data-testid="slot-assign-btn">
          {saving ? '...' : <><Check size={16} className="mr-1" /> {slot.occupied ? 'Update' : 'Store Vial'}</>}
        </Button>
        {slot.occupied && (
          <Button variant="outline" onClick={clear} disabled={clearing}
            className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="slot-clear-btn">
            <Trash size={16} />
          </Button>
        )}
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================
const LN2StoragePage = () => {
  const [towers, setTowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [activeTower, setActiveTower] = useState('A');
  const [activeBox, setActiveBox] = useState(null);
  const [boxData, setBoxData] = useState(null);
  const [loadingBox, setLoadingBox] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [search, setSearch] = useState('');

  const loadTowers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getLN2Towers();
      setTowers(data || []);
    } catch { toast.error('Failed to load storage'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTowers(); }, [loadTowers]);

  const initStorage = async () => {
    setInitializing(true);
    try {
      await api.initLN2Storage();
      toast.success('LN2 storage initialized');
      loadTowers();
    } catch { toast.error('Failed to initialize'); }
    finally { setInitializing(false); }
  };

  const loadBox = useCallback(async (tower, box) => {
    setLoadingBox(true);
    setSelectedSlot(null);
    try {
      const data = await api.getLN2Box(tower, box);
      setBoxData(data);
      setActiveBox(box);
    } catch { toast.error('Failed to load box'); }
    finally { setLoadingBox(false); }
  }, []);

  const currentTower = towers.find(t => t.tower === activeTower);
  const isInitialized = currentTower?.initialized;
  const anyInitialized = towers.some(t => t.initialized);

  const totalOccupied = towers.reduce((s, t) => s + (t.occupied || 0), 0);
  const totalSlots = towers.reduce((s, t) => s + (t.total_slots || 0), 0);

  return (
    <div data-testid="ln2-storage-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">LN2 Cryostorage</h1>
          <p className="text-slate-500 mt-1">8 towers (A-H) · 10 boxes per tower · 81 slots per box (9x9)</p>
        </div>
        {!anyInitialized && (
          <Button className="btn-primary" onClick={initStorage} disabled={initializing} data-testid="init-storage-btn">
            {initializing ? 'Initializing...' : <><Snowflake size={16} className="mr-2" /> Initialize Storage</>}
          </Button>
        )}
      </div>

      {/* Stats bar */}
      {anyInitialized && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-cyan-400">{totalOccupied}</p>
            <p className="text-xs text-slate-500">Vials Stored</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-slate-300">{totalSlots - totalOccupied}</p>
            <p className="text-xs text-slate-500">Empty Slots</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-indigo-400">{totalSlots}</p>
            <p className="text-xs text-slate-500">Total Capacity</p>
          </div>
          <div className="card-flat p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{totalSlots > 0 ? Math.round((totalOccupied / totalSlots) * 100) : 0}%</p>
            <p className="text-xs text-slate-500">Utilization</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="spinner" /></div>
      ) : !anyInitialized ? (
        <div className="text-center py-16">
          <Snowflake size={56} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 text-lg">No cryostorage initialized</p>
          <p className="text-sm text-slate-500 mt-1">Click "Initialize Storage" to set up 8 towers with 10 boxes each</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Tower + Box selector */}
          <div className="lg:col-span-1 space-y-4">
            {/* Tower tabs */}
            <div>
              <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Tower</Label>
              <div className="grid grid-cols-8 gap-1" data-testid="tower-selector">
                {TOWERS.map(t => {
                  const tw = towers.find(x => x.tower === t);
                  const occ = tw?.occupied || 0;
                  const total = tw?.total_slots || 810;
                  const pct = total > 0 ? Math.round((occ / total) * 100) : 0;
                  return (
                    <button
                      key={t}
                      onClick={() => { setActiveTower(t); setActiveBox(null); setBoxData(null); setSelectedSlot(null); }}
                      className={`ln2-tower-btn ${activeTower === t ? 'ln2-tower-btn--active' : ''}`}
                      data-testid={`tower-btn-${t}`}
                    >
                      <span className="text-sm font-bold">{t}</span>
                      <span className="text-[10px] text-slate-500">{pct}%</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Box grid for selected tower */}
            {currentTower && (
              <div>
                <Label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
                  Tower {activeTower} — Boxes
                </Label>
                <div className="grid grid-cols-5 gap-2" data-testid="box-selector">
                  {(currentTower.boxes || []).map(bx => {
                    const pct = bx.total > 0 ? Math.round((bx.occupied / bx.total) * 100) : 0;
                    const isActive = activeBox === bx.box;
                    return (
                      <button
                        key={bx.box}
                        onClick={() => loadBox(activeTower, bx.box)}
                        className={`ln2-box-btn ${isActive ? 'ln2-box-btn--active' : ''}`}
                        data-testid={`box-btn-${bx.box}`}
                      >
                        <span className="text-xs font-semibold">{bx.label}</span>
                        <div className="w-full h-1 rounded-full bg-slate-700 mt-1">
                          <div className="h-full rounded-full bg-cyan-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-slate-500">{bx.occupied}/{bx.total}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/30 border border-slate-700/20">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-slate-700/60 border border-slate-600/40" />
                <span className="text-xs text-slate-500">Empty</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-cyan-500/30 border border-cyan-500/50" />
                <span className="text-xs text-slate-500">Occupied</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-indigo-500/40 border-2 border-indigo-400" />
                <span className="text-xs text-slate-500">Selected</span>
              </div>
            </div>
          </div>

          {/* RIGHT: 9x9 grid + slot panel */}
          <div className="lg:col-span-2">
            {activeBox && boxData ? (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setActiveBox(null); setBoxData(null); setSelectedSlot(null); }}
                      className="text-slate-500 hover:text-slate-300">
                      <ArrowLeft size={18} />
                    </button>
                    <h3 className="text-base font-semibold text-slate-100">
                      Box {boxData.label}
                    </h3>
                    <span className="text-xs text-slate-500">
                      {boxData.slots.filter(s => s.occupied).length} / {boxData.slots.length} occupied
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MagnifyingGlass size={14} className="text-slate-500" />
                    <Input value={search} onChange={e => setSearch(e.target.value)}
                      placeholder="Search vials..." className="input-field w-40 text-xs" data-testid="slot-search" />
                  </div>
                </div>

                <div className="flex gap-4">
                  <SlotGrid
                    slots={boxData.slots}
                    onSlotClick={(slot) => setSelectedSlot(slot.slot === selectedSlot ? null : slot.slot)}
                    selectedSlot={selectedSlot}
                    searchTerm={search}
                  />

                  {/* Slot detail panel */}
                  {selectedSlot && (
                    <div className="flex-shrink-0 w-72">
                      <SlotPanel
                        tower={activeTower}
                        boxNum={activeBox}
                        slot={boxData.slots.find(s => s.slot === selectedSlot)}
                        onClose={() => setSelectedSlot(null)}
                        onRefresh={() => {
                          loadBox(activeTower, activeBox);
                          loadTowers();
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : loadingBox ? (
              <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
            ) : (
              <div className="flex items-center justify-center h-64 rounded-xl bg-slate-800/20 border border-slate-700/15">
                <div className="text-center">
                  <GridFour size={48} className="mx-auto text-slate-600 mb-2" />
                  <p className="text-sm text-slate-500">Select a box from Tower {activeTower} to view slots</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LN2StoragePage;
